/* The cockpit's view of who is signed in.
 *
 * One context, read by three places: `AuthGuard` decides whether to render a
 * screen, `TopBar` shows the name, `LoginPage` starts the flow. Everything
 * behind it lives in `session.ts` and, beyond that, in the Python service —
 * this file's whole job is to make one external store legible to React.
 *
 * `useSyncExternalStore` rather than `useState` plus an effect: the session is a
 * single fact about the application rather than a piece of one component's
 * state, and every reader has to see the same answer on the same render.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactElement,
  type ReactNode,
} from 'react';

import {
  beginLogin,
  beginLogout,
  chooseTenant,
  loadSession,
  snapshot,
  subscribe,
  type SessionState,
  type SessionUser,
} from './session';

interface AuthContextValue {
  /** `pending` until `/auth/me` has answered, which is not the same as signed
      out — see `SessionState`. */
  readonly status: SessionState['status'];
  readonly isAuthenticated: boolean;
  /** Signed in *and* scoped to a tenant. The two are separate questions, and
      `TenantGuard` is the reader of the second one: an operator between the
      callback and the picker is fully authenticated and can fetch nothing. */
  readonly hasTenant: boolean;
  readonly user: SessionUser | null;
  /** Leaves the page. `next` is where to land once the sign-in completes. */
  readonly login: (next?: string) => void;
  /** Also leaves the page: signing out goes through Entra ID. */
  readonly logout: () => void;
  /** Re-seals the session cookie against one tenant. Rejects with an `ApiError`
      where the operator holds no grant for it. Does *not* leave the page — the
      caller is responsible for emptying the caches that hold the previous
      tenant's rows. */
  readonly selectTenant: (customerId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const session = useSyncExternalStore(subscribe, snapshot);

  /* The only place the probe is fired. Under StrictMode in dev this runs twice;
     two GETs of the same read are harmless, and a guard against them would also
     prevent the re-probe that a later sign-out needs. */
  useEffect(() => {
    void loadSession();

    function revalidateRestoredPage(event: PageTransitionEvent): void {
      if (event.persisted) {
        void loadSession();
      }
    }

    window.addEventListener('pageshow', revalidateRestoredPage);
    return () => {
      window.removeEventListener('pageshow', revalidateRestoredPage);
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status: session.status,
      isAuthenticated: session.status === 'authenticated',
      hasTenant: session.user?.tenantSelected === true,
      user: session.user,
      login: beginLogin,
      logout: beginLogout,
      selectTenant: async (customerId: string) => {
        await chooseTenant(customerId);
      },
    }),
    [session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Returns the auth context. Throws if called outside `AuthProvider`. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}

export type { SessionUser };
